import { Check, ChevronsDown } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { useHtzAhuComponentKindListQuery } from "@/services/backend/htz/ahu/api/component-kind-api";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/shared/components/ui/command";
import { useState } from "react";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Label } from "@/shared/components/ui/label";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import { InputErrorMessages } from "@/shared/components/errors/input-error-messages";
export function ComponentKindsSelect({
  companyId,
  componentKinds,
  onComponentKindsChange,
  errors,
}: {
  companyId: string;
  componentKinds: string[];
  onComponentKindsChange: (cks: string[]) => void;
  errors: string[];
}) {
  const [open, setOpen] = useState(false);
  const {
    data: list,
    isLoading,
    error,
  } = useHtzAhuComponentKindListQuery({
    company: { active: true, values: [companyId] },
  });

  // Narrow client-side as well, so only kinds of the catalog's company can be
  // selected regardless of server-side filter support.
  const kinds = (list?.kinds ?? []).filter(
    (kind) => kind.companyId === companyId,
  );

  if (isLoading) {
    return null;
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <div className="grid gap-1.5">
      <Label>{t("Relevante Arten von Komponenten")}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="justify-between"
          >
            <span>
              {t("Komponentenarten")} ({componentKinds?.length ?? 0})
            </span>
            <ChevronsDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0">
          <Command>
            <CommandInput placeholder={t("Suchen")} />
            <CommandEmpty>{t("Nicht gefunden.")}</CommandEmpty>
            <CommandGroup>
              {kinds.map((kind) => (
                <CommandItem
                  key={kind.id}
                  value={kind.name}
                  onSelect={() => {
                    if (componentKinds.some((k) => k === kind.id)) {
                      onComponentKindsChange(
                        componentKinds.filter((k) => k !== kind.id),
                      );
                    } else {
                      onComponentKindsChange([...componentKinds, kind.id]);
                    }
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      componentKinds?.includes(kind.id)
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  {kind.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
      <span className="text-muted-foreground text-xs">
        {t(
          "Die Vorlage wird nur bei den ausgewählten Komponentenarten angezeigt.",
        )}
      </span>
      <InputErrorMessages errors={errors} />
    </div>
  );
}
